
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useBrandSelection } from "@/hooks/useBrandSelection";

interface GrowthSquadMember {
  id: string;
  name: string;
  alias: string;
  email: string;
  user_id: string;
  avatar?: string;
  avatar_url?: string;
  brand_id: string;
  type: 'Growth Partner' | 'Client' | 'Third Party';
  slack_id?: string;
  origin_brand?: string;
  role?: string;
  created_at: string;
  updated_at: string;
}

export const useGrowthSquad = () => {
  const [squadMembers, setSquadMembers] = useState<GrowthSquadMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentBrandId } = useBrandSelection();


  const fetchSquadMembers = async () => {
    if (!currentBrandId) {
      setSquadMembers([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("growth_squad")
        .select("*")
        .eq("brand_id", currentBrandId)
        .order("name", { ascending: true });

      if (error) {
        throw error;
      }

      // Fetch avatar URLs from profiles and roles from user_brand_access for each member with a user_id
      const userIds = (data || []).filter(m => m.user_id).map(m => m.user_id);
      let avatarMap: Record<string, string> = {};
      let roleMap: Record<string, string> = {};
      
      if (userIds.length > 0) {
        // Fetch profiles for avatars
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, avatar_url")
          .in("id", userIds);
        
        if (profiles) {
          const supabaseUrl = 'https://hjodtxoylcvcwjgvuctk.supabase.co';
          
          avatarMap = profiles.reduce((acc, p) => {
            if (p.avatar_url) {
              acc[p.id] = p.avatar_url;
            } else {
              // Construct avatar URL from storage if profile avatar_url is null
              acc[p.id] = `${supabaseUrl}/storage/v1/object/public/avatars/${p.id}/avatar.png`;
            }
            return acc;
          }, {} as Record<string, string>);
        }

        // Fetch roles from user_brand_access
        const { data: accessData } = await supabase
          .from("user_brand_access")
          .select("user_id, role")
          .eq("brand_id", currentBrandId)
          .in("user_id", userIds);
        
        if (accessData) {
          roleMap = accessData.reduce((acc, a) => {
            acc[a.user_id] = a.role;
            return acc;
          }, {} as Record<string, string>);
        }
      }

      setSquadMembers((data || []).map(member => ({
        ...member,
        type: member.type as 'Growth Partner' | 'Client' | 'Third Party',
        avatar_url: member.user_id ? avatarMap[member.user_id] || undefined : undefined,
        role: member.user_id ? roleMap[member.user_id] || undefined : undefined,
      })));
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch growth squad members",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const syncMemberToContact = async (
    brandId: string,
    email: string,
    name: string,
    memberId: string
  ): Promise<string | null> => {
    if (!email?.trim()) return null;

    const nameParts = (name || '').trim().split(' ');
    const firstName = nameParts[0] || null;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

    try {
      const { data: contactId, error } = await supabase.rpc('upsert_os_contact', {
        p_brand_id: brandId,
        p_email: email,
        p_phone: null,
        p_first_name: firstName,
        p_last_name: lastName,
        p_organization: null,
        p_title: null,
        p_user_id: null,
        p_module: ['GrowthOS'],
        p_activity: ['squad member'],
        p_source_table: 'growth_squad',
        p_source_id: memberId,
      });

      if (error) {
        console.error('Error syncing squad member to contacts:', error);
        return null;
      }

      return contactId;
    } catch (err) {
      console.error('Error syncing squad member to contacts:', err);
      return null;
    }
  };

  const createSquadMember = async (memberData: Omit<GrowthSquadMember, "id" | "created_at" | "updated_at" | "avatar">) => {
    if (!currentBrandId) {
      toast({
        title: "Error",
        description: "No brand selected",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { data, error } = await supabase
        .from("growth_squad")
        .insert([{
          ...memberData,
          brand_id: currentBrandId,
          avatar: memberData.alias
        }])
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Sync to contacts
      const contactId = await syncMemberToContact(
        currentBrandId,
        data.email,
        data.name,
        data.id
      );

      if (contactId) {
        await supabase
          .from("growth_squad")
          .update({ contact_id: contactId })
          .eq("id", data.id);
      }

      await fetchSquadMembers();
      toast({
        title: "Success",
        description: "Squad member created successfully",
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create squad member",
        variant: "destructive",
      });
      return false;
    }
  };

  const updateSquadMember = async (id: string, updateData: Partial<GrowthSquadMember>) => {
    try {
      const { data, error } = await supabase
        .from("growth_squad")
        .update({
          ...updateData,
          avatar: updateData.alias || updateData.avatar
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Re-sync to contacts if email or name changed
      if (updateData.email || updateData.name) {
        const contactId = await syncMemberToContact(
          data.brand_id,
          data.email,
          data.name,
          id
        );

        if (contactId && contactId !== data.contact_id) {
          await supabase
            .from("growth_squad")
            .update({ contact_id: contactId })
            .eq("id", id);
        }
      }

      await fetchSquadMembers();
      toast({
        title: "Success",
        description: "Squad member updated successfully",
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update squad member",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteSquadMember = async (id: string) => {
    try {
      const { error } = await supabase
        .from("growth_squad")
        .delete()
        .eq("id", id);

      if (error) {
        throw error;
      }

      await fetchSquadMembers();
      toast({
        title: "Success",
        description: "Squad member deleted successfully",
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete squad member",
        variant: "destructive",
      });
      return false;
    }
  };

  const syncAllToContacts = async () => {
    if (squadMembers.length === 0) return;

    let successCount = 0;
    for (const member of squadMembers) {
      const contactId = await syncMemberToContact(
        member.brand_id,
        member.email,
        member.name,
        member.id
      );

      if (contactId) {
        await supabase
          .from("growth_squad")
          .update({ contact_id: contactId })
          .eq("id", member.id);
        successCount++;
      }
    }

    toast({
      title: "Sync Complete",
      description: `${successCount} of ${squadMembers.length} squad members synced to contacts`,
    });

    await fetchSquadMembers();
  };

  useEffect(() => {
    fetchSquadMembers();
  }, [currentBrandId]);

  // Filter squad members created by current user
  const userSquadMembers = squadMembers.filter(member => {
    const isCreatedByUser = member.user_id === user?.id;
    return isCreatedByUser;
  });

  return {
    squadMembers,
    userSquadMembers,
    isLoading,
    createSquadMember,
    updateSquadMember,
    deleteSquadMember,
    syncAllToContacts,
    refetch: fetchSquadMembers,
  };
};
